import Widget from "components/dashboard/widget";
import React, { useState, useEffect } from "react";

import { ReactComponent as ArrowDownIcon } from "assets/icons/ico_arrow_down_green.svg";
import { ReactComponent as ArrowUpIcon } from "assets/icons/ico_arrow_up_green.svg";
import { ReactComponent as PenIcon } from "assets/icons/ico_pen.svg";
import { getToken } from "context/auth";
import { useTranslation } from "react-i18next";
import { useMessages } from "context/messages";
import { useParams } from "react-router-dom";
import ButtonSlider from "components/common/buttonSlider";

import ButtonText from "../../components/common/buttonText";
import {
  addParkingAreaQueueSettings,
  getParkingAreaQueueSettings,
} from "api/permits";
import { PermitQueuePreview } from "./permitQueuePreview";
import { useSearchParams } from "react-router-dom";
import InputNumber from "components/common/inputNumber";
import { ReactComponent as InfoIcon } from "assets/icons/ico_info.svg";
import SelectParkingSpotsModal from "components/dashboard/selectParkingSpotsModal/selectParkingSpotsModal";
import CommunitiesModal from "pages/communities/communitiesModal";
import { getParkingSpotsWithNumberOfPermitsForParkingArea } from 'api/permits';
import { getCommunitiesForParkingArea } from "api/communities";

const PermitQueueSettings = ({ initialSettings, initialParkingSpots }) => {
  const { t } = useTranslation();
  const { showToast } = useMessages();

  const fetchParkingSpots = async () => {
    try {
      const token = await getToken();
      const result = await getParkingSpotsWithNumberOfPermitsForParkingArea(token, parkingAreaExtId);

      if (result && !result.error) {
        setParkingSpots(result.filter(spot => spot.price != null && spot.price !== 0));
        setInitSelectedParkingSpots(result.filter((spot) => spot.isAutomatic));
        return result; // Return the result for potential chaining
      } else {
        console.error("Failed to fetch parking spots:", result);
        showToast(
          t("generic.errorTitle"),
          t("generic.errorDescription"),
          "error"
        );
        return [];
      }
    } catch (error) {
      console.error("Error fetching parking spots:", error);
      showToast(
        t("generic.errorTitle"),
        t("generic.errorDescription"),
        "error"
      );
      return [];
    }
  };
  const fetchCommunities = async () => {
    try {
      var token = await getToken();
      var communitiesForParkingAreaResult = await getCommunitiesForParkingArea(
        token,
        parkingAreaExtId
      );

      if (
        communitiesForParkingAreaResult &&
        !communitiesForParkingAreaResult.error
      ) {
        setCommunities(communitiesForParkingAreaResult || []);
      } else {
        console.error(communitiesForParkingAreaResult);
        showToast(
          t("generic.errorTitle"),
          t("generic.errorDescription"),
          "error"
        );
      }
    } catch (error) {
      console.error(error);
      showToast(
        t("generic.errorTitle"),
        t("generic.errorDescription"),
        "error"
      );
    }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [openSettings, setOpenSettings] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [initSelectedParkingSpots, setInitSelectedParkingSpots] = useState([]);
  const [initQueueConfig, setInitQueueConfig] = useState({
    isAreaParkingQueueEnabled: false,
    isSpecificSpotsQueueEnabled: true,
    isQueueLengthInfoPublic: false,
    isUserQueuePositionInfoPublic: false,
    isParkingAreaAutomatic: false,
    parkingAreaAutomaticQueueHours: 48,
    cancellationPeriodInMonths: 1,
  });
  const [queueConfig, setQueueConfig] = useState({
    isAreaParkingQueueEnabled: false,
    isSpecificSpotsQueueEnabled: true,
    isQueueLengthInfoPublic: false,
    isUserQueuePositionInfoPublic: false,
    isParkingAreaAutomatic: false,
    parkingAreaAutomaticQueueHours: 48,
    parkingSpots: [],
    cancellationPeriodInMonths: 1,
  });
  const [displayParkingSpotsModal, setDisplayParkingSpotsModal] = useState(false);
  const [displayCommunitiesModal, setDisplayCommunitiesModal] = useState(false);
  const [parkingSpots, setParkingSpots] = useState([]);
  const [communities, setCommunities] = useState([]);
  const { parkingAreaExtId } = useParams();

  // Use props from parent if available (loaded via single API call)
  useEffect(() => {
    if (initialSettings) {
      // Områden utan konfigurerad uppsägningstid (null) visas som default 1 månad — samma
      // fallback som API:et använder. Sätts även i initQueueConfig så att fältet inte
      // felaktigt markerar formuläret som ändrat direkt vid inläsning.
      const cancellationPeriodInMonths = initialSettings.cancellationPeriodInMonths || 1;
      setQueueConfig({
        ...initialSettings,
        parkingAreaAutomaticQueueHours: initialSettings.parkingAreaAutomaticQueueHours || 48,
        cancellationPeriodInMonths,
      });
      setInitQueueConfig({ ...initialSettings, cancellationPeriodInMonths });
    }
  }, [initialSettings]);

  useEffect(() => {
    if (initialParkingSpots) {
      setParkingSpots(initialParkingSpots.filter(spot => spot.price != null && spot.price !== 0));
      setInitSelectedParkingSpots(initialParkingSpots.filter((spot) => spot.isAutomatic));
    }
  }, [initialParkingSpots]);

  useEffect(() => {
    if (!parkingAreaExtId) return;

    // Only fetch parking spots and settings if not provided via props
    if (!initialParkingSpots) {
      fetchParkingSpots();
    }
    if (!initialSettings) {
      const fetchQueueSettings = async () => {
        try {
          const token = await getToken();
          const queueSettings = await getParkingAreaQueueSettings(token, parkingAreaExtId);
          if (queueSettings && !queueSettings.error) {
            setQueueConfig({
              ...queueSettings,
              parkingAreaAutomaticQueueHours: queueSettings.parkingAreaAutomaticQueueHours || 48,
            });
            setInitQueueConfig({ ...queueSettings });
          } else {
            console.error(queueSettings?.error);
            showToast(
              t("generic.errorTitle"),
              t("permitQueueTable.queueCantBeFetched"),
              "error"
            );
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          showToast(
            t("generic.errorTitle"),
            t("generic.errorDescription"),
            "error"
          );
        }
      };
      fetchQueueSettings();
    }

    // Communities always fetched separately (different API)
    fetchCommunities();
  }, [parkingAreaExtId]);

  useEffect(() => {
    setSearchParams({
      positionInQueue: queueConfig.isUserQueuePositionInfoPublic,
    });
  }, [queueConfig.isUserQueuePositionInfoPublic]);

  useEffect(() => {
    const isPreviewHidden = Object.values(queueConfig)
      .filter((value) => typeof value == "boolean")
      .every((value) => value === false);
    setIsPreviewVisible(!isPreviewHidden);
  }, [queueConfig]);

  const handleQueueSettingsSave = async () => {
    try {
      const token = await getToken();

      // Sista klampningen av uppsägningstiden: fältet kan stå tomt om användaren
      // sparar utan att lämna det, och API:et avvisar värden under 1.
      const noticeMonths = parseInt(queueConfig.cancellationPeriodInMonths, 10);

      const payload = {
        ...queueConfig,
        cancellationPeriodInMonths: Number.isNaN(noticeMonths) || noticeMonths < 1 ? 1 : noticeMonths,
        communityExtIds: queueConfig.isCommunityMembersOnly ? (queueConfig.communityExtIds || []) : null,
      };

      await addParkingAreaQueueSettings(token, parkingAreaExtId, payload);
      setInitSelectedParkingSpots(queueConfig.parkingSpots);
      showToast(
        t("permitQueuePage.queueSettingsSavedTitle"),
        t("permitQueuePage.queueSettingsSavedDescription"),
        "success"
      );
    } catch (error) {
      console.error(error);
      showToast(
        t("generic.errorTitle"),
        t("generic.errorDescription"),
        "error"
      );
    }
  };

  const handleAutomaticQueueModal = (isEdit) => {
    if (isEdit) {
      setDisplayParkingSpotsModal(true);
    } else {
      setDisplayParkingSpotsModal(!queueConfig.isParkingAreaAutomatic);
    }
    let updatedSpots = [];
    if (!displayParkingSpotsModal && !isEdit) {
      updatedSpots = parkingSpots.filter((spot) => spot.isAutomatic).map((spot) => ({ ...spot, isAutomatic: false }));
      setParkingSpots(parkingSpots.map((spot) => ({ ...spot, isAutomatic: false })));
    }
    setQueueConfig({
      ...queueConfig,
      parkingSpots: updatedSpots,
      isAreaParkingQueueEnabled: true,
      isParkingAreaAutomatic: isEdit ? queueConfig.isParkingAreaAutomatic : !queueConfig.isParkingAreaAutomatic,
    });
  };

  const handleCommunityMembersOnlyToggle = (isEdit) => {
    if (isEdit) {
      setDisplayCommunitiesModal(true);
    } else {
      setDisplayCommunitiesModal(!queueConfig.isCommunityMembersOnly);
    }
    setQueueConfig({
      ...queueConfig,
      isAreaParkingQueueEnabled: true,
      isCommunityMembersOnly: isEdit ? queueConfig.isCommunityMembersOnly : !queueConfig.isCommunityMembersOnly
    });
  };

  useEffect(() => {
    if(!queueConfig.isAreaParkingQueueEnabled){
      setQueueConfig({
        ...queueConfig,
        isSpecificSpotsQueueEnabled: false,
        isQueueLengthInfoPublic: false,
        isUserQueuePositionInfoPublic: false,
        isParkingAreaAutomatic: false,
        parkingAreaAutomaticQueueHours: 48,
        // cancellationPeriodInMonths nollställs INTE här — uppsägningstiden gäller alla
        // tillstånd i området och är oberoende av om kön är påslagen.
        parkingSpots: parkingSpots?.map(spot => ({ ...spot, isAutomatic: false })),
      });
    }
  }, [queueConfig.isAreaParkingQueueEnabled])

  return (
    <>
      <Widget className="mt-6">
        <div>
          <button
            className="w-full text-left px-1 py-3 flex justify-between items-center text-gray-700"
            onClick={() => setOpenSettings(!openSettings)}
          >
            <p className="text-xl font-medium">
              {t("permitQueuePage.queueSettings")}
            </p>
            <span>{openSettings ? <ArrowUpIcon /> : <ArrowDownIcon />}</span>
          </button>
          <div className="flex justify-between w-full flex-col md:flex-row gap-4">
            <div className={`${openSettings ? "block" : "hidden"} p-1 md:p-4`}>
              <div className="mb-4">
                <div className="mb-4">
                  <ButtonSlider
                    title={t("permitQueuePage.allowUsersToJoinParkingQueue")}
                    isToggled={queueConfig.isAreaParkingQueueEnabled}
                    onClick={() => 
                      setQueueConfig({
                        ...queueConfig,
                        isAreaParkingQueueEnabled:
                        !queueConfig.isAreaParkingQueueEnabled,
                      })
                    }
                  />
                </div>
              </div>
              <hr className="mb-4"></hr>
              <div className="mb-4">
                <ButtonSlider
                  title={t("permitQueuePage.allowUsersToSeeQueueCount")}
                  isToggled={queueConfig.isQueueLengthInfoPublic}
                  onClick={() =>
                    setQueueConfig({
                      ...queueConfig,
                      isAreaParkingQueueEnabled: true,
                      isQueueLengthInfoPublic:
                        !queueConfig.isQueueLengthInfoPublic,
                    })
                  }
                />
              </div>
              <div className="mb-4">
                <ButtonSlider
                  title={t("permitQueuePage.allowUsersToSeeQueuePosition")}
                  isToggled={queueConfig.isUserQueuePositionInfoPublic}
                  onClick={() => {
                    setQueueConfig({
                      ...queueConfig,
                      isAreaParkingQueueEnabled: true,
                      isUserQueuePositionInfoPublic:
                        !queueConfig.isUserQueuePositionInfoPublic,
                    });
                  }}
                />
              </div>

              <div className="mb-4">
                <div className="flex gap-2">
                  <ButtonSlider
                    title={t("permitQueuePage.enableCommunityMembersOnly")}
                    isToggled={queueConfig.isCommunityMembersOnly}
                    onClick={() => handleCommunityMembersOnlyToggle(false)}
                  />
                  {queueConfig.isCommunityMembersOnly && <div className="flex-1 items-center flex gap-2" onClick={() => handleCommunityMembersOnlyToggle(true)}>
                    <PenIcon width={16}></PenIcon>
                  </div>}
                </div>

              </div>
              <div className="mb-4">
                <div className="flex gap-2">
                  <div className="flex items-center gap-4">
                    <ButtonSlider
                      title={t('permitQueuePage.enableAutomaticQueueForParkingArea')}
                      isToggled={queueConfig.isParkingAreaAutomatic}
                      onClick={() => handleAutomaticQueueModal(false)}
                    />
                  </div>
                  {queueConfig.isParkingAreaAutomatic && <div className="flex-1 items-center flex gap-2" onClick={() => handleAutomaticQueueModal(true)}>
                    <PenIcon width={16}></PenIcon>
                  </div>}
                </div>
                <SelectParkingSpotsModal
                  display={displayParkingSpotsModal}
                  setDisplay={setDisplayParkingSpotsModal}
                  parkingSpots={parkingSpots || []}
                  selectedParkingSpots={parkingSpots?.filter((spot) => spot.isAutomatic)}
                  showPrice
                  setSelectedParkingSpots={(selectedSpots) => {
                    // Create sets of extIds for comparison
                    const newSelectedIds = new Set(selectedSpots.map(s => s.parkingSpotExtId));
                    const currentSelectedIds = new Set(
                      parkingSpots.filter(spot => spot.isAutomatic).map(s => s.parkingSpotExtId)
                    );

                    // Create updated parking spots array without mutation
                    const updatedParkingSpots = parkingSpots.map(spot => {
                      if (newSelectedIds.has(spot.parkingSpotExtId)) {
                        // This spot is now selected
                        return { ...spot, isAutomatic: true };
                      } else if (currentSelectedIds.has(spot.parkingSpotExtId) && !newSelectedIds.has(spot.parkingSpotExtId)) {
                        // This spot was selected before but is now deselected
                        return { ...spot, isAutomatic: false };
                      }
                      return spot;
                    });

                    setParkingSpots(updatedParkingSpots);

                    // Update config with spots that have isAutomatic = true
                    const updatedSpots = updatedParkingSpots.filter(spot => spot.isAutomatic);

                    setQueueConfig({
                      ...queueConfig,
                      parkingSpots: updatedParkingSpots,
                      isParkingAreaAutomatic: updatedSpots.length > 0,
                    });
                  }}
                  isSingleSelectionOnly={false}
                >
                  <div className="flex justify-center">
                    <hr className="w-80 mb-2 mt-2" />
                  </div>


                </SelectParkingSpotsModal>

                <CommunitiesModal
                  display={displayCommunitiesModal}
                  setDisplay={setDisplayCommunitiesModal}
                  communities={communities || []}
                  selectedCommunities={queueConfig.communityExtIds || []}
                  setSelectedCommunities={(selectedCommunityIds) => {
                    setQueueConfig({
                      ...queueConfig,
                      communityExtIds: selectedCommunityIds,
                      isCommunityMembersOnly: (selectedCommunityIds || []).length > 0,
                    });
                  }}
                  isSingleSelectionOnly={false}
                >
                  <div className="flex justify-center">
                    <hr className="w-80 mb-2 mt-2" />
                  </div>


                </CommunitiesModal>
                {queueConfig.isParkingAreaAutomatic && (
                  <div className="mt-4 text-sm text-gray-600 flex flex-col gap-2 bg-gray-100 rounded-md p-2">
                    <div className="flex flex-row items-center">
                      <InfoIcon className="h-8 w-8 mr-2" />
                    <p>
                      {t("permitQueuePage.disclaimerDescriptionAutomaticQueue")}
                    </p>
                    </div>
                  </div>
                )}
              </div>
              {queueConfig.isParkingAreaAutomatic && (
                <div className="mb-4 flex items-center gap-2">
                  <label className="font-medium text-gray-700 mb-0">
                    {t("permitQueuePage.numberOfHoursToAcceptDeclinePermit")}
                  </label>
                  <InputNumber
                    onChange={(value) => {
                      if (
                        value !== queueConfig.parkingAreaAutomaticQueueHours &&
                        value !== 0
                      ) {
                        setQueueConfig({
                          ...queueConfig,
                          parkingAreaAutomaticQueueHours: value,
                        });
                      } else if (value === 0) {
                        setQueueConfig({
                          ...queueConfig,
                          parkingAreaAutomaticQueueHours: 1,
                        });
                      }
                    }}
                    min={1}
                    value={queueConfig.parkingAreaAutomaticQueueHours}
                  />
                </div>
              )}
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <label className="font-medium text-gray-700 mb-0">
                    {t("permitQueuePage.cancellationPeriodInMonths")}
                  </label>
                  <InputNumber min={1}
                    value={queueConfig.cancellationPeriodInMonths}
                    onChange={(value) => {
                      // Klampa INTE här — då går ettan inte att sudda eller skriva över,
                      // eftersom det tomma mellanläget snappar tillbaka direkt.
                      setQueueConfig({
                        ...queueConfig,
                        cancellationPeriodInMonths: value === '' ? '' : Number(value),
                      });
                    }}
                    onBlur={(value) => {
                      const months = parseInt(value, 10);
                      setQueueConfig({
                        ...queueConfig,
                        cancellationPeriodInMonths: Number.isNaN(months) || months < 1 ? 1 : months,
                      });
                    }}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  {t("permitQueuePage.cancellationPeriodInMonthsDescription")}
                </p>
              </div>
              {openSettings && (
                <div className="flex justify-end">
                  <ButtonText
                    onClick={async () => {
                      handleQueueSettingsSave();
                    }}
                    isLoading={false}
                    className="w-24 mt-4"
                  >
                    {t("permitQueueItemModal.buttonSave")}
                  </ButtonText>
                  <ButtonText
                    isDelete={true}
                    className="w-24 mt-4 ml-2"
                    onClick={() => {
                      setQueueConfig(initQueueConfig);
                      setParkingSpots(parkingSpots.map(spot => ({ ...spot, isAutomatic: initSelectedParkingSpots.some(s => s.parkingSpotExtId === spot.parkingSpotExtId) })));
                    }}
                    disabled={
                      JSON.stringify(initQueueConfig) ===
                      JSON.stringify(queueConfig)
                    }
                  >
                    {t("permitQueueItemModal.buttonCancel")}
                  </ButtonText>
                </div>
              )}
            </div>
            {openSettings && isPreviewVisible && (
              <PermitQueuePreview
                queueConfig={queueConfig}
              ></PermitQueuePreview>
            )}
          </div>
        </div>

        {/* <div className="flex justify-between w-full flex-col md:flex-row gap-4"></div> */}
      </Widget>

    </>
  );
};

export default PermitQueueSettings;
